import { MonthlyReportHelper } from "@deathstar/types/blazar";
import {
    CoverageOption,
    Driver,
    formatVIN,
    MonthlyReport,
    MonthlyReportData,
    MonthlyReportDataUnit,
    PolicyAdditionalInterest,
    Tractor,
    Trailer,
} from "@deathstar/types/northstar";
import { formatLicense } from "@deathstar/types/waypoint";
import { ActionButton, classNames, DataTable, Popover, Tabs, TabsProps } from "@deathstar/ui";
import {
    BackspaceIcon,
    BuildingLibraryIcon,
    CalendarIcon,
    EllipsisVerticalIcon,
    HashtagIcon,
    PencilSquareIcon,
    PlusCircleIcon,
    PlusIcon,
} from "@heroicons/react/24/outline";
import { ShieldCheckIcon } from "@heroicons/react/24/solid";
import { Tooltip } from "@material-ui/core";
import { ColumnDef, getCoreRowModel, getFilteredRowModel, getSortedRowModel, useReactTable } from "@tanstack/react-table";
import moment from "moment";
import { useMemo, useState } from "react";
import api from "../../../api/api";
import { queryClient } from "../../../util/queryClient";
import { stringifyAddress } from "../../certificates/certificatesUtil";
import { DriverTableMeta, employerColumn, linkedTractorsColumn, linkedTrailersColumn } from "../../drivers/DriverColumns";
import { NewDriverDialog } from "../../drivers/NewDriverDialog";
import { OwnerOperatorEmployerLinker } from "../../drivers/OwnerOperatorEmployerLinker";
import RemoveDriverDialog from "../../drivers/RemoveDriverDialog";
import { TractorLinker, TrailerLinker } from "../../drivers/TractorLinker";
import {
    getDriverChangeDetail,
    getEquipmentChangeDetail,
    getPendingDriverChanges,
    getPendingEquipmentChanges,
    getPendingPolicyChangeRowClass,
    PendingPolicyChangeIndicator,
    usePendingPolicyChanges,
} from "../../pending-policy-changes/PendingPolicyChanges";
import { NewUnitDialog } from "../NewUnitDialog";
import RemoveUnitDialog from "../RemoveUnitDialog";

type TableMeta = {
    policyId: string;
    reportYear: number;
    reportMonth: number;
    isAdvancedReporter: boolean;
    isFinalized: boolean;
} & Pick<DriverTableMeta, "openEmployerLinker" | "companyName">;

export function MonthlyReportUnitsTable({ data, report }: { data: MonthlyReportData; report: MonthlyReport }) {
    const [typeSelected, setTypeSelected] = useState<"tractors" | "trailers" | "drivers">(() =>
        data.isDriverReporter
            ? "drivers"
            : data.equipment.tractors.length > 0
            ? "tractors"
            : data.equipment.trailers.length > 0
            ? "trailers"
            : "tractors"
    );
    const units = useMemo(() => MonthlyReportHelper.getUnitsForCoverage(data, typeSelected, undefined), [data, typeSelected]);
    const [adding, setAdding] = useState(false);
    const [linkingEmployerToDriverId, setLinkingEmployerToDriverId] = useState<number | null>(null);
    const [linkingTractorToDriverId, setLinkingTractorToDriverId] = useState<number | null>(null);
    const [linkingTrailerToDriverId, setLinkingTrailerToDriverId] = useState<number | null>(null);
    const updateDriver = api.drivers.useUpdate(data.endLayer.policy.accountId);
    const pendingPolicyChanges = usePendingPolicyChanges();

    const columns = useMemo(() => (typeSelected === "drivers" ? driverColumns : tractorTrailerColumns), [typeSelected]);
    const meta = useMemo<TableMeta>(
        () => ({
            isFinalized: report.isFinalized,
            policyId: report.policyId,
            reportYear: report.year,
            reportMonth: report.month,
            isAdvancedReporter: data.isAdvancedReporter,
            openEmployerLinker(driverId: number) {
                const found = units.find((row) => row.id === driverId);
                if (found && found.isOwnerOperator) {
                    setLinkingEmployerToDriverId(driverId);
                }
            },
            openTractorLinker(driverId: number) {
                const found = units.find((row) => row.id === driverId);
                if (found) {
                    setLinkingTractorToDriverId(driverId);
                }
            },
            openTrailerLinker(driverId: number) {
                const found = units.find((row) => row.id === driverId);
                if (found) {
                    setLinkingTrailerToDriverId(driverId);
                }
            },
            companyName: data.endLayer.policy.firstNamedInsured,
        }),
        [
            data.endLayer.policy.firstNamedInsured,
            data.isAdvancedReporter,
            report.isFinalized,
            report.month,
            report.policyId,
            report.year,
            units,
        ]
    );
    const table = useReactTable({
        data: units as MonthlyReportDataUnit<Tractor | Trailer | Driver>[],
        columns: columns as ColumnDef<MonthlyReportDataUnit<Tractor | Trailer | Driver>>[],
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        meta,
    });

    return (
        <div className="w-full min-w-0 grow overflow-y-auto overflow-x-hidden">
            <DataTable
                table={table}
                className="!col-spacing-2 !row-spacing-1"
                getRowClassName={(unit) => {
                    const isAdded = unit.isNewToPolicy;
                    const isRemoved = unit.isRemovedFromPolicy;
                    const isAddedAndRemoved = isAdded && isRemoved;
                    const changes = unit.changes;

                    const pendingChanges =
                        typeSelected === "drivers"
                            ? getPendingDriverChanges(pendingPolicyChanges.changes, unit.accountId, unit.id)
                            : getPendingEquipmentChanges(pendingPolicyChanges.changes, unit.accountId, typeSelected, unit.id);

                    return classNames(
                        isAddedAndRemoved
                            ? "bg-yellow-400/10"
                            : isAdded
                            ? "bg-emerald-400/10"
                            : isRemoved
                            ? "bg-red-400/10"
                            : changes.length > 0
                            ? "bg-yellow-400/10"
                            : "",
                        getPendingPolicyChangeRowClass(pendingChanges),
                        "last:border-b-0"
                    );
                }}
                headerRow={
                    <div className="flex flex-wrap items-center gap-4">
                        <Tabs<"tractors" | "trailers" | "drivers">
                            borderClasses="border-waypoint-blue"
                            tabs={
                                [
                                    data.isDriverReporter && {
                                        label: () => (
                                            <div
                                                className={classNames(
                                                    "w-max rounded px-2 py-1 text-stone-600 transition-colors hover:bg-black/5 sm:w-32",
                                                    typeSelected === "drivers" && "!text-waypoint-blue"
                                                )}
                                            >
                                                Drivers ({data.equipment.activeDrivers.length})
                                            </div>
                                        ),
                                        value: "drivers",
                                    },
                                    data.equipment.tractors.length > 0 && {
                                        label: () => (
                                            <div
                                                className={classNames(
                                                    "w-max rounded px-2 py-1 text-stone-600 transition-colors hover:bg-black/5 sm:w-32",
                                                    typeSelected === "tractors" && "!text-waypoint-blue"
                                                )}
                                            >
                                                Tractors ({data.equipment.activeTractors.length})
                                            </div>
                                        ),
                                        value: "tractors",
                                    },
                                    data.equipment.trailers.length > 0 && {
                                        label: () => (
                                            <div
                                                className={classNames(
                                                    "w-max rounded px-2 py-1 text-stone-600 transition-colors hover:bg-black/5 sm:w-32",
                                                    typeSelected === "trailers" && "!text-waypoint-blue"
                                                )}
                                            >
                                                Trailers ({data.equipment.activeTrailers.length})
                                            </div>
                                        ),
                                        value: "trailers",
                                    },
                                ].filter(Boolean) as TabsProps<"tractors" | "trailers" | "drivers">["tabs"]
                            }
                            value={typeSelected}
                            onChange={(value) => {
                                setTypeSelected(value);
                            }}
                            className="hidden rounded-lg bg-stone-100/60 px-2 py-1 font-light sm:block"
                        />
                        <select
                            className="sm:hidden"
                            value={typeSelected}
                            onChange={(e) => setTypeSelected(e.target.value as typeof typeSelected)}
                        >
                            {data.equipment.tractors.length > 0 && (
                                <option value="tractors">Tractors ({data.equipment.activeTractors.length})</option>
                            )}
                            {data.equipment.trailers.length > 0 && (
                                <option value="trailers">Trailers ({data.equipment.activeTrailers.length})</option>
                            )}
                            {data.isDriverReporter && <option value="drivers">Drivers ({data.equipment.activeDrivers.length})</option>}
                        </select>
                        <div className="flex grow items-center justify-end gap-2">
                            <Tooltip
                                title={
                                    report.isSubmitted || report.isFinalized
                                        ? "This report has already been submitted and cannot be changed. Ask your account manager or agent to allow for resubmission, if desired."
                                        : ""
                                }
                            >
                                <div className="">
                                    <ActionButton
                                        onClick={() => setAdding(true)}
                                        className="hidden sm:flex"
                                        disabled={report.isSubmitted || report.isFinalized}
                                    >
                                        <PlusCircleIcon className="h-4 w-4" />
                                        Add {typeSelected === "tractors" ? "tractor" : typeSelected === "trailers" ? "trailer" : "driver"}
                                    </ActionButton>
                                    <button
                                        onClick={() => setAdding(true)}
                                        className="rounded border border-blue-600 p-1 text-blue-600 disabled:opacity-50 sm:hidden"
                                        disabled={report.isSubmitted || report.isFinalized}
                                    >
                                        <PlusIcon className="h-4 w-4" />
                                    </button>
                                </div>
                            </Tooltip>

                            <DataTable.Search
                                table={table}
                                classes={{
                                    inputContainer: classNames(
                                        "transition-all py-1 sm:py-2 px-1 sm:px-2",
                                        table.getState().globalFilter ? "w-32" : "w-8 !px-2"
                                    ),
                                    inputContainerFocused: "!w-32",
                                }}
                            />
                        </div>
                    </div>
                }
            />
            {(typeSelected === "tractors" || typeSelected === "trailers") && (
                <NewUnitDialog
                    open={adding}
                    onClose={() => setAdding(false)}
                    unitType={typeSelected}
                    dialogProps={{
                        baseZIndex: 100,
                    }}
                    defaultValues={{
                        atDate: moment
                            .utc()
                            .set({ year: report.year, month: report.month - 1, date: 1 })
                            .format("YYYY-MM-DD"),
                    }}
                    getExistingUnit={(vin: string) => {
                        return (units as MonthlyReportDataUnit<Tractor>[]).find((r) => formatVIN(r.vin) === formatVIN(vin));
                    }}
                    deferPolicyChanges
                />
            )}

            {typeSelected === "drivers" && (
                <>
                    <NewDriverDialog
                        open={adding}
                        onClose={() => setAdding(false)}
                        dialogProps={{
                            baseZIndex: 100,
                        }}
                        defaultValues={{
                            atDate: moment
                                .utc()
                                .set({ year: report.year, month: report.month - 1, date: 1 })
                                .format("YYYY-MM-DD"),
                        }}
                        getExistingDriver={(license: string) => {
                            return (units as MonthlyReportDataUnit<Driver>[]).find(
                                (r) => formatLicense(r.license) === formatLicense(license)
                            );
                        }}
                        deferPolicyChanges
                    />
                    <OwnerOperatorEmployerLinker
                        open={!!linkingEmployerToDriverId}
                        currentValue={
                            (units as MonthlyReportDataUnit<Driver>[]).find((r) => r.id === linkingEmployerToDriverId)?.employerId || null
                        }
                        onClose={() => setLinkingEmployerToDriverId(null)}
                        onChange={async (employer) => {
                            if (linkingEmployerToDriverId) {
                                updateDriver.mutate(
                                    { id: linkingEmployerToDriverId, employer: employer || null },
                                    {
                                        onSuccess: () => {
                                            if (employer && typeof employer === "object") {
                                                queryClient.invalidateQueries({
                                                    queryKey: api.ownerOperatorEmployers.queryKeys.get(data.endLayer.policy.accountId),
                                                });
                                                queryClient.invalidateQueries({
                                                    queryKey: api.certificates.queryKeys.find(data.endLayer.policy.accountId),
                                                });
                                            }
                                        },
                                    }
                                );
                                setLinkingEmployerToDriverId(null);
                            }
                        }}
                        driver={units.find((r) => r.id === linkingEmployerToDriverId) as MonthlyReportDataUnit<Driver>}
                    />
                    <TractorLinker
                        open={!!linkingTractorToDriverId}
                        currentValue={
                            (units as MonthlyReportDataUnit<Driver>[]).find((r) => r.id === linkingTractorToDriverId)?.tractors || []
                        }
                        onClose={() => setLinkingTractorToDriverId(null)}
                        onChange={async (tractors) => {
                            if (linkingTractorToDriverId) {
                                updateDriver.mutate({ id: linkingTractorToDriverId, tractors });
                                setLinkingTractorToDriverId(null);
                            }
                        }}
                    />
                    <TrailerLinker
                        open={!!linkingTrailerToDriverId}
                        currentValue={
                            (units as MonthlyReportDataUnit<Driver>[]).find((r) => r.id === linkingTrailerToDriverId)?.trailers || []
                        }
                        onClose={() => setLinkingTrailerToDriverId(null)}
                        onChange={async (trailers) => {
                            if (linkingTrailerToDriverId) {
                                updateDriver.mutate({ id: linkingTrailerToDriverId, trailers });
                                setLinkingTrailerToDriverId(null);
                            }
                        }}
                    />
                </>
            )}
        </div>
    );
}

function getDefaultUpdateDate(data: { reportYear: number; reportMonth: number; isAdvancedReporter: boolean }): moment.Moment {
    if (data.isAdvancedReporter) {
        // start of the month
        return moment
            .utc()
            .set({ year: data.reportYear, month: data.reportMonth - 1, date: 1, hour: 12, minute: 0, second: 0, millisecond: 0 });
    } else {
        // end of the month
        return moment
            .utc()
            .set({ year: data.reportYear, month: data.reportMonth - 1, date: 1, hour: 12, minute: 0, second: 0, millisecond: 0 })
            .endOf("month");
    }
}

const tractorTrailerColumns: ColumnDef<MonthlyReportDataUnit<Tractor | Trailer>>[] = [
    {
        id: "mobile",
        cell: function MobileTractorTrailerCell(info) {
            const meta = info.table.options.meta as TableMeta;
            const defaultUpdateDate = getDefaultUpdateDate(meta);
            const unit = info.row.original;
            const type = unit.isTractor ? "tractors" : "trailers";
            const [changeDialogOpen, setChangeDialogOpen] = useState(false);
            const [removeDialogOpen, setRemoveDialogOpen] = useState(false);

            return (
                <>
                    <Popover>
                        <Popover.Button className="w-full text-left">
                            <div className="flex items-center justify-between">
                                <p className="font-medium">{unit.unitNumber}</p>
                                <p className="tabular-nums">
                                    {unit.isACV
                                        ? "ACV"
                                        : unit.statedValue?.toLocaleString("en-us", {
                                              style: "currency",
                                              currency: "USD",
                                              maximumFractionDigits: 0,
                                          })}
                                </p>
                            </div>
                            <p className="font-mono uppercase text-stone-600">
                                <HashtagIcon className="-mt-1 mr-1 inline-block h-3 w-3" />
                                {unit.vin}
                            </p>
                            {!!unit.coverages?.filter((c) => !c.isUpcoming).length && (
                                <p className="text-stone-600">
                                    <ShieldCheckIcon className="-mt-1 mr-1 inline-block h-3 w-3 text-black/30" />
                                    {unit.coverages
                                        .filter((c) => !c.isUpcoming)
                                        .map((c) => CoverageOption.getMetadata(c.coverageOptionId).defaultAbbreviation)
                                        .join(", ")}
                                </p>
                            )}
                        </Popover.Button>
                        <Popover.Content className="w-3/4">
                            <div>
                                <div>
                                    <p className="text-stone-600">
                                        <span>
                                            {unit.year} {unit.make}
                                        </span>{" "}
                                        - <span className="capitalize">{unit.type?.name}</span>
                                    </p>
                                </div>
                                {unit.additionalInterests.length > 0 && (
                                    <div>
                                        {unit.additionalInterests.map((a) => (
                                            <div key={a.id}>
                                                <p>{a.additionalInterest?.name}</p>
                                                <p>
                                                    {[
                                                        a.isAdditionalInsured && "Additional Insured",
                                                        a.isLossPayee && "Loss Payee",
                                                        a.isMortgagee && "Mortgagee",
                                                        a.isPrimaryAndNonContributory && "Primary and Non-Contributory",
                                                        a.isWaiverOfSubrogation && "Waiver of Subrogation",
                                                    ]
                                                        .filter(Boolean)
                                                        .join(", ")}
                                                </p>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <hr className="my-2" />
                            <Popover.Item
                                onClick={() => {
                                    setChangeDialogOpen(true);
                                }}
                                disabled={!!unit.pendingChanges?.length}
                            >
                                <PencilSquareIcon className="h-5 w-5" />
                                <span>Update{unit.pendingChanges?.length ? " (pending)" : ""}</span>
                            </Popover.Item>
                            <Popover.Item
                                onClick={() => {
                                    setRemoveDialogOpen(true);
                                }}
                                disabled={!!unit.pendingChanges?.length}
                            >
                                <BackspaceIcon className="h-5 w-5" />
                                <span>Remove</span>
                            </Popover.Item>
                        </Popover.Content>
                    </Popover>
                    <NewUnitDialog
                        open={changeDialogOpen}
                        onClose={() => setChangeDialogOpen(false)}
                        unitType={type}
                        defaultValues={{
                            atDate: defaultUpdateDate.format("YYYY-MM-DD"),
                            coverages: info.row.original.coverages,
                            vin: info.row.original.vin ?? undefined,
                            year: info.row.original.year ?? undefined,
                            make: info.row.original.make ?? undefined,
                            value: info.row.original.policyValue ?? undefined,
                            acv: info.row.original.policyAcv ?? undefined,
                            typeId: info.row.original.typeId ?? undefined,
                            unitNumber: info.row.original.unitNumber ?? undefined,
                            ownerOperator: info.row.original.policyOwnerOperator ?? undefined,
                        }}
                        editingId={info.row.original.id}
                        dialogProps={{ baseZIndex: 100 }}
                        coverageFilter={(c) => c.policyId === meta.policyId}
                        deferPolicyChanges
                        pendingChangeDetail={getEquipmentChangeDetail(info.row.original.vin)}
                    />
                    <RemoveUnitDialog
                        open={removeDialogOpen}
                        onClose={() => {
                            setRemoveDialogOpen(false);
                        }}
                        accountId={info.row.original.accountId}
                        unitId={info.row.original.id}
                        unitType={type}
                        coverages={info.row.original.coverages}
                        defaultDate={defaultUpdateDate.format("YYYY-MM-DD")}
                        deferPolicyChanges
                        pendingChangeDetail={getEquipmentChangeDetail(info.row.original.vin)}
                    />
                </>
            );
        },
        header: () => null,
        meta: { responsive: true, classes: { header: "hidden" } },
    },
    {
        id: "browserPending",
        header: () => null,
        cell: function BrowserPendingCell(info) {
            const { changes } = usePendingPolicyChanges();
            const unit = info.row.original;

            return (
                <PendingPolicyChangeIndicator
                    changes={getPendingEquipmentChanges(changes, unit.accountId, unit.isTractor ? "tractors" : "trailers", unit.id)}
                />
            );
        },
        enableGlobalFilter: false,
        enableSorting: false,
        meta: { classes: { cell: "w-8" } },
    },
    {
        id: "changes",
        accessorKey: "changes",
        enableGlobalFilter: false,
        header: "",
        enableSorting: false,
        cell: (info) => {
            if (info.row.original.changes.length > 0) {
                return <ChangeListTooltip unit={info.row.original} />;
            }
            return null;
        },
    },
    {
        id: "hasImportantPendingChanges",
        accessorFn: (row) => row.pendingChanges,
        enableGlobalFilter: false,
        header: () => null,
        cell: (info) => {
            const meta = info.table.options.meta as TableMeta;
            const coverageChanges =
                info.row.original.pendingChanges?.filter(
                    (c) => !c.isCompleted && !c.isRejected && c.isImportant && (!c.policyId || meta.policyId === c.policyId)
                ) || [];
            if (coverageChanges.length) {
                return (
                    <Tooltip
                        title={
                            <div>
                                <p>Pending changes</p>
                                <ul>
                                    {coverageChanges.map((change) => (
                                        <li key={change.id}>{change.descriptionShort}</li>
                                    ))}
                                </ul>
                            </div>
                        }
                    >
                        <p className="w-max whitespace-nowrap rounded-full bg-yellow-50 px-2 text-yellow-700">
                            <span className="border-b border-dashed border-stone-300">Pending changes</span>
                        </p>
                    </Tooltip>
                );
            }
            return null;
        },
        enableColumnFilter: false,
    },
    {
        id: "unitNumber",
        accessorKey: "unitNumber",
        header: "Unit",
        meta: {
            classes: {
                cell: "tabular-nums uppercase",
            },
        },
    },
    {
        id: "year",
        accessorKey: "year",
        enableGlobalFilter: false,
        meta: {
            classes: {
                cell: "tabular-nums",
            },
        },
    },
    {
        id: "make",
        accessorKey: "make",
    },
    {
        id: "type",
        accessorFn: (row) => row.type?.name || "",
    },
    {
        id: "vin",
        accessorKey: "vin",
        meta: {
            classes: {
                cell: "tabular-nums uppercase",
            },
        },
    },
    {
        id: "value",
        accessorFn: (row) =>
            row.isACV
                ? "ACV"
                : row.statedValue?.toLocaleString("en-us", {
                      style: "currency",
                      currency: "USD",
                      maximumFractionDigits: 0,
                  }),
        enableGlobalFilter: false,
    },
    {
        id: "coverages",
        accessorFn: (row) =>
            row.coverages
                ?.filter((c) => !c.isUpcoming)
                .map((c) => CoverageOption.getMetadata(c.coverageOptionId).defaultAbbreviation)
                .join(", "),
        enableGlobalFilter: false,
    },
    {
        id: "additionalInterests",
        accessorKey: "additionalInterests",
        header: "LP",
        cell: (info) => {
            const value = info.getValue() as PolicyAdditionalInterest[];
            if (value.length) {
                return (
                    <Tooltip
                        title={
                            <ul>
                                {value.map((ai) => (
                                    <li key={ai.id}>
                                        {ai.additionalInterest?.name || "[Unknown]"} -{" "}
                                        {[
                                            ai.isAdditionalInsured && "Additional Insured",
                                            ai.isLossPayee && "Loss Payee",
                                            ai.isMortgagee && "Mortgagee",
                                            ai.isPrimaryAndNonContributory && "Primary and Non-Contributory",
                                            ai.isWaiverOfSubrogation && "Waiver of Subrogation",
                                        ]
                                            .filter(Boolean)
                                            .join(", ")}
                                    </li>
                                ))}
                            </ul>
                        }
                    >
                        <BuildingLibraryIcon className="h-4 w-4" />
                    </Tooltip>
                );
            }
            return null;
        },
        enableGlobalFilter: false,
    },
    {
        id: "actions",
        cell: (info) => {
            const meta = info.table.options.meta as TableMeta;
            const defaultUpdateDate = getDefaultUpdateDate(meta);
            return (
                <TractorTrailerActions
                    unit={info.row.original}
                    type={info.row.original.isTractor ? "tractors" : "trailers"}
                    defaultUpdateDate={defaultUpdateDate.format("YYYY-MM-DD")}
                    policyId={meta.policyId}
                />
            );
        },
        enableGlobalFilter: false,
    },
];

const driverColumns: ColumnDef<MonthlyReportDataUnit<Driver>>[] = [
    {
        id: "mobile",
        cell: function MobileDriverCell(info) {
            const meta = info.table.options.meta as TableMeta;
            const unit = info.row.original;
            const [changeDialogOpen, setChangeDialogOpen] = useState(false);
            const [removeDialogOpen, setRemoveDialogOpen] = useState(false);

            const defaultUpdateDate = moment
                .utc()
                .set({ year: meta.reportYear, month: meta.reportMonth - 1, date: 1, hour: 12, minute: 0, second: 0, millisecond: 0 });
            return (
                <>
                    <Popover>
                        <Popover.Button className="w-full text-left">
                            <div className="flex flex-wrap items-center justify-between gap-2">
                                <p className="font-medium">{unit.name}</p>
                                <p className="text-stone-600">{unit.dob ? moment(unit.dob).format("MM/DD/YYYY") : null}</p>
                            </div>
                            <p className="text-stone-600">
                                <CalendarIcon className="-mt-1 mr-1 inline-block h-3 w-3" />
                                Hired on {unit.doh ? moment(unit.doh).format("MM/DD/YYYY") : "Unknown"}
                            </p>
                            <p className="font-mono uppercase text-stone-600">
                                <HashtagIcon className="-mt-1 mr-1 inline-block h-3 w-3" />
                                {unit.license}
                            </p>
                            {!!unit.coverages?.filter((c) => !c.isUpcoming).length && (
                                <p className="text-stone-600">
                                    <ShieldCheckIcon className="-mt-1 mr-1 inline-block h-3 w-3 text-black/30" />
                                    {unit.coverages
                                        .filter((c) => !c.isUpcoming)
                                        .map((c) => CoverageOption.getMetadata(c.coverageOptionId).defaultAbbreviation)
                                        .join(", ")}
                                </p>
                            )}
                            {unit.policyOwnerOperator ? (
                                <p className="overflow-hidden text-ellipsis whitespace-nowrap text-stone-500">
                                    Employed by {meta.companyName}
                                </p>
                            ) : unit.employer ? (
                                <Tooltip
                                    classes={{ tooltip: "bg-white shadow-lg rounded p-2 text-stone-800 font-normal border" }}
                                    title={
                                        <div>
                                            <p>{unit.employer.name}</p>
                                            <p>{stringifyAddress(unit.employer)}</p>
                                            <p>{unit.employer.email}</p>
                                            <p>{unit.employer.fax}</p>
                                        </div>
                                    }
                                >
                                    <p className="overflow-hidden text-ellipsis whitespace-nowrap text-stone-500">
                                        Employed by {unit.employer.name}
                                    </p>
                                </Tooltip>
                            ) : (
                                <p className="overflow-hidden text-ellipsis whitespace-nowrap text-yellow-600">Employer not set</p>
                            )}
                        </Popover.Button>
                        <Popover.Content className="w-3/4">
                            <Popover.Item
                                onClick={() => {
                                    setChangeDialogOpen(true);
                                }}
                                disabled={!!unit.pendingChanges?.length}
                            >
                                <PencilSquareIcon className="h-5 w-5" />
                                <span>Update{unit.pendingChanges?.length ? " (pending)" : ""}</span>
                            </Popover.Item>
                            <Popover.Item
                                onClick={() => {
                                    setRemoveDialogOpen(true);
                                }}
                                disabled={!!unit.pendingChanges?.length}
                            >
                                <BackspaceIcon className="h-5 w-5" />
                                <span>Remove</span>
                            </Popover.Item>
                        </Popover.Content>
                    </Popover>
                    <NewDriverDialog
                        open={changeDialogOpen}
                        onClose={() => setChangeDialogOpen(false)}
                        defaultValues={{
                            atDate: defaultUpdateDate.format("YYYY-MM-DD"),
                            coverages: unit.coverages,
                            dob: info.row.original.dob || "",
                            doh: info.row.original.policyDoh || "",
                            exp: info.row.original.policyExp || 0,
                            license: info.row.original.license || "",
                            name: info.row.original.name || "",
                            state: info.row.original.state || "",
                            ownerOperator: !!info.row.original.policyOwnerOperator,
                        }}
                        editingId={unit.id}
                        dialogProps={{ baseZIndex: 100 }}
                        coverageFilter={(c) => c.policyId === meta.policyId}
                        deferPolicyChanges
                        pendingChangeDetail={getDriverChangeDetail(info.row.original.name)}
                    />
                    <RemoveDriverDialog
                        open={removeDialogOpen}
                        onClose={() => {
                            setRemoveDialogOpen(false);
                        }}
                        accountId={info.row.original.accountId}
                        unitId={info.row.original.id}
                        coverages={info.row.original.coverages}
                        defaultDate={defaultUpdateDate.format("YYYY-MM-DD")}
                        deferPolicyChanges
                        pendingChangeDetail={getDriverChangeDetail(info.row.original.name)}
                    />
                </>
            );
        },
        header: () => null,
        meta: { responsive: true, classes: { header: "hidden" } },
    },
    {
        id: "browserPending",
        header: () => null,
        cell: function BrowserPendingCell(info) {
            const { changes } = usePendingPolicyChanges();
            const unit = info.row.original;

            return <PendingPolicyChangeIndicator changes={getPendingDriverChanges(changes, unit.accountId, unit.id)} />;
        },
        enableGlobalFilter: false,
        enableSorting: false,
        meta: { classes: { cell: "w-8" } },
    },
    {
        id: "changes",
        accessorKey: "changes",
        enableGlobalFilter: false,
        header: "",
        enableSorting: false,
        cell: (info) => {
            if (info.row.original.changes.length > 0) {
                return <ChangeListTooltip unit={info.row.original} />;
            }
            return null;
        },
    },
    {
        id: "hasImportantPendingChanges",
        accessorFn: (row) => row.pendingChanges,
        enableGlobalFilter: false,
        header: () => null,
        cell: (info) => {
            const meta = info.table.options.meta as TableMeta;
            const coverageChanges =
                info.row.original.pendingChanges?.filter(
                    (c) => !c.isCompleted && !c.isRejected && c.isImportant && (!c.policyId || meta.policyId === c.policyId)
                ) || [];
            if (coverageChanges.length) {
                return (
                    <Tooltip
                        title={
                            <div>
                                <p>Pending changes</p>
                                <ul>
                                    {coverageChanges.map((change) => (
                                        <li key={change.id}>{change.descriptionShort}</li>
                                    ))}
                                </ul>
                            </div>
                        }
                    >
                        <p className="w-max whitespace-nowrap rounded-full bg-yellow-50 px-2 text-yellow-700">
                            <span className="border-b border-dashed border-stone-300">Pending changes</span>
                        </p>
                    </Tooltip>
                );
            }
            return null;
        },
        enableColumnFilter: false,
    },
    {
        id: "name",
        accessorKey: "name",
    },
    {
        id: "license",
        accessorKey: "license",
        meta: {
            classes: {
                cell: "tabular-nums uppercase",
            },
        },
    },
    {
        id: "dob",
        accessorKey: "dob",
        meta: {
            classes: {
                cell: "tabular-nums",
            },
        },
        cell: (info) => (info.getValue() ? moment(info.getValue() as string).format("MM/DD/YYYY") : null),
    },
    {
        id: "doh",
        header: "Hired",
        accessorKey: "doh",
        meta: {
            classes: {
                cell: "tabular-nums",
            },
        },
        cell: (info) => (info.getValue() ? moment(info.getValue() as string).format("MM/DD/YYYY") : null),
    },
    {
        id: "coverages",
        accessorFn: (row) =>
            row.coverages
                ?.filter((c) => !c.isUpcoming)
                .map((c) => CoverageOption.getMetadata(c.coverageOptionId).defaultAbbreviation)
                .join(", "),
        enableGlobalFilter: false,
    },
    employerColumn as ColumnDef<MonthlyReportDataUnit<Driver>>,
    linkedTractorsColumn as ColumnDef<MonthlyReportDataUnit<Driver>>,
    linkedTrailersColumn as ColumnDef<MonthlyReportDataUnit<Driver>>,
    {
        id: "actions",
        cell: (info) => {
            const meta = info.table.options.meta as TableMeta;
            const defaultUpdateDate = getDefaultUpdateDate(meta);
            return (
                <DriverActions
                    unit={info.row.original}
                    defaultUpdateDate={defaultUpdateDate.format("YYYY-MM-DD")}
                    policyId={meta.policyId}
                />
            );
        },
        enableGlobalFilter: false,
    },
];

function ChangeListTooltip({ unit }: { unit: MonthlyReportDataUnit<Tractor | Trailer | Driver> }) {
    const isAdded = unit.isNewToPolicy;
    const isRemoved = unit.isRemovedFromPolicy;
    const isAddedAndRemoved = isAdded && isRemoved;
    return (
        <Tooltip
            placement="right-start"
            classes={{
                tooltip: "bg-white rounded text-black border border-stone-100 p-3 shadow-lg !max-w-none font-normal",
            }}
            title={
                <table className="col-spacing-2">
                    <thead>
                        <tr>
                            <th className="text-left font-normal text-stone-500">Date</th>
                            <th className="text-left font-normal text-stone-500">Coverage</th>
                            <th className="text-left font-normal text-stone-500">Description</th>
                        </tr>
                    </thead>
                    <tbody>
                        {unit.changes
                            .flatMap((change) =>
                                Array.from(change.changesByCoverage.entries()).flatMap(([coverageOptionId, c]) =>
                                    c.changes.map((ch) => [change.date, coverageOptionId, ch] as const)
                                )
                            )
                            .sort((a, b) => b[0].getTime() - a[0].getTime())
                            .map(([date, coverageOptionId, change], i) => (
                                <tr key={i}>
                                    <td className="text-left">{moment.utc(date, "YYYY-MM-DD").format("MM/DD/YY")}</td>
                                    <td>{CoverageOption.getMetadata(coverageOptionId).defaultAbbreviation}</td>
                                    <td className="text-left">{change.description}</td>
                                </tr>
                            ))}
                    </tbody>
                </table>
            }
        >
            {isAddedAndRemoved ? (
                <span className="cursor-default text-yellow-600">△</span>
            ) : isAdded ? (
                <span className="cursor-default text-emerald-600">+</span>
            ) : isRemoved ? (
                <span className="cursor-default text-red-600">-</span>
            ) : unit.changes.length > 0 ? (
                <span className="cursor-default text-yellow-600">△</span>
            ) : (
                <span></span>
            )}
        </Tooltip>
    );
}

function TractorTrailerActions({
    unit,
    type,
    defaultUpdateDate,
    policyId,
}: {
    unit: MonthlyReportDataUnit<Tractor | Trailer>;
    type: "tractors" | "trailers";
    defaultUpdateDate: string;
    policyId: string;
}) {
    const [changeDialogOpen, setChangeDialogOpen] = useState(false);
    const [removeDialogOpen, setRemoveDialogOpen] = useState(false);

    return (
        <>
            <Popover>
                <Popover.Content>
                    <Popover.Item
                        onClick={() => {
                            setChangeDialogOpen(true);
                        }}
                        disabled={!!unit.pendingChanges?.length}
                    >
                        <PencilSquareIcon className="h-5 w-5" />
                        <span>Update{unit.pendingChanges?.length ? " (pending)" : ""}</span>
                    </Popover.Item>
                    <Popover.Item
                        onClick={() => {
                            setRemoveDialogOpen(true);
                        }}
                        disabled={!!unit.pendingChanges?.length}
                    >
                        <BackspaceIcon className="h-5 w-5" />
                        <span>Remove</span>
                    </Popover.Item>
                </Popover.Content>
                <Popover.Button className="rounded p-1 hover:bg-black/5">
                    <EllipsisVerticalIcon className="h-5 w-5" />
                </Popover.Button>
            </Popover>
            <NewUnitDialog
                open={changeDialogOpen}
                onClose={() => setChangeDialogOpen(false)}
                unitType={type}
                defaultValues={{
                    atDate: defaultUpdateDate,
                    coverages: unit.coverages,
                    vin: unit.vin ?? undefined,
                    year: unit.year ?? undefined,
                    make: unit.make ?? undefined,
                    value: unit.statedValue ?? undefined,
                    acv: unit.isACV ?? undefined,
                    typeId: unit.typeId ?? undefined,
                    unitNumber: unit.unitNumber ?? undefined,
                    ownerOperator: unit.isOwnerOperator ?? undefined,
                }}
                editingId={unit.id}
                dialogProps={{ baseZIndex: 100 }}
                coverageFilter={(c) => c.policyId === policyId}
                deferPolicyChanges
                pendingChangeDetail={getEquipmentChangeDetail(unit.vin)}
            />
            <RemoveUnitDialog
                open={removeDialogOpen}
                onClose={() => {
                    setRemoveDialogOpen(false);
                }}
                accountId={unit.accountId}
                unitId={unit.id}
                unitType={type}
                coverages={unit.coverages}
                defaultDate={defaultUpdateDate}
                deferPolicyChanges
                pendingChangeDetail={getEquipmentChangeDetail(unit.vin)}
            />
        </>
    );
}

function DriverActions({
    unit,
    defaultUpdateDate,
    policyId,
}: {
    unit: MonthlyReportDataUnit<Driver>;
    defaultUpdateDate: string;
    policyId: string;
}) {
    const [changeDialogOpen, setChangeDialogOpen] = useState(false);
    const [removeDialogOpen, setRemoveDialogOpen] = useState(false);

    return (
        <>
            <Popover>
                <Popover.Content>
                    <Popover.Item
                        onClick={() => {
                            setChangeDialogOpen(true);
                        }}
                        disabled={!!unit.pendingChanges?.length}
                    >
                        <PencilSquareIcon className="h-5 w-5" />
                        <span>Update{unit.pendingChanges?.length ? " (pending)" : ""}</span>
                    </Popover.Item>
                    <Popover.Item
                        onClick={() => {
                            setRemoveDialogOpen(true);
                        }}
                        disabled={!!unit.pendingChanges?.length}
                    >
                        <BackspaceIcon className="h-5 w-5" />
                        <span>Remove</span>
                    </Popover.Item>
                </Popover.Content>
                <Popover.Button className="rounded p-1 hover:bg-black/5">
                    <EllipsisVerticalIcon className="h-5 w-5" />
                </Popover.Button>
            </Popover>
            <NewDriverDialog
                open={changeDialogOpen}
                onClose={() => setChangeDialogOpen(false)}
                defaultValues={{
                    atDate: defaultUpdateDate,
                    coverages: unit.coverages,
                    dob: unit.dob || "",
                    doh: unit.policyDoh || "",
                    exp: unit.policyExp || 0,
                    license: unit.license || "",
                    name: unit.name || "",
                    state: unit.state || "",
                    ownerOperator: !!unit.isOwnerOperator,
                }}
                editingId={unit.id}
                dialogProps={{ baseZIndex: 100 }}
                coverageFilter={(c) => c.policyId === policyId}
                deferPolicyChanges
                pendingChangeDetail={getDriverChangeDetail(unit.name)}
            />
            <RemoveDriverDialog
                open={removeDialogOpen}
                onClose={() => {
                    setRemoveDialogOpen(false);
                }}
                accountId={unit.accountId}
                unitId={unit.id}
                coverages={unit.coverages}
                defaultDate={defaultUpdateDate}
                deferPolicyChanges
                pendingChangeDetail={getDriverChangeDetail(unit.name)}
            />
        </>
    );
}
